import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import { getEnv } from '@unni/platform'

import { cn } from '@/shared/utils'
import { useLocales } from '@/shared/hooks/useLocales'
import { postAmplitude } from '@/core/lib'
import { localeToCountry } from '@/utils/language'

const HIDE_KEY = 'ugFab.hiddenUntil'
const HIDE_MS = 1 * 24 * 60 * 60 * 1000 // 1 days

const useHiddenUntil = () => {
  const [hidden, setHidden] = useState<boolean>(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const until = Number(window.localStorage.getItem(HIDE_KEY) || 0)
    if (Number.isFinite(until) && until > Date.now()) {
      setHidden(true)
    }
  }, [])

  const hideForDuration = useCallback((ms: number) => {
    if (typeof window === 'undefined') return
    const until = Date.now() + ms
    window.localStorage.setItem(HIDE_KEY, String(until))
    setHidden(true)
  }, [])

  return { hidden, hideForDuration }
}

const CloseIcon = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.80298 2.80304C3.09588 2.51015 3.57075 2.51015 3.86364 2.80304L17.197 16.1364C17.4899 16.4293 17.4899 16.9041 17.197 17.197C16.9041 17.4899 16.4292 17.4899 16.1363 17.197L2.80298 3.8637C2.51009 3.57081 2.51009 3.09594 2.80298 2.80304Z"
      fill="#222222"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.197 2.80304C17.4899 3.09594 17.4899 3.57081 17.197 3.8637L3.86364 17.197C3.57075 17.4899 3.09588 17.4899 2.80298 17.197C2.51009 16.9041 2.51009 16.4293 2.80298 16.1364L16.1363 2.80304C16.4292 2.51015 16.9041 2.51015 17.197 2.80304Z"
      fill="#222222"
    />
  </svg>
)

const UnniIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16.2002 2.25C17.0277 2.25 17.6936 2.24904 18.2314 2.29297C18.778 2.33763 19.2579 2.43288 19.7021 2.65918C20.4077 3.0187 20.9813 3.59225 21.3408 4.29785C21.5671 4.74205 21.6624 5.22195 21.707 5.76856C21.751 6.30639 21.75 6.97222 21.75 7.79981V13.2002C21.75 14.0278 21.751 14.6936 21.707 15.2314C21.6624 15.7781 21.5671 16.258 21.3408 16.7022C20.9813 17.4078 20.4077 17.9813 19.7021 18.3408C19.2579 18.5671 18.778 18.6624 18.2314 18.707C17.6936 18.751 17.0277 18.75 16.2002 18.75H13.6836C13.0296 18.75 12.8062 18.7535 12.5996 18.7959C12.4011 18.8367 12.2087 18.9039 12.0283 18.9961C11.8406 19.092 11.6639 19.2293 11.1533 19.6377L8.76854 21.5459C8.57232 21.7029 8.38467 21.8534 8.22362 21.96C8.07407 22.0588 7.82056 22.2096 7.50096 22.21C7.12045 22.2102 6.76082 22.0366 6.52342 21.7393C6.32404 21.4895 6.2828 21.1981 6.26659 21.0195C6.24916 20.8273 6.24998 20.5871 6.24999 20.3359V18.7451C5.75771 18.7379 5.37291 18.7141 5.02928 18.6221C3.73534 18.2753 2.72469 17.2647 2.37791 15.9707C2.2437 15.4698 2.24999 14.8817 2.24999 14V7.79981C2.24998 6.97222 2.24902 6.30639 2.29295 5.76856C2.33761 5.22195 2.43286 4.74205 2.65916 4.29785C3.01868 3.59226 3.59225 3.01871 4.29784 2.65918C4.74202 2.43287 5.22195 2.33763 5.76854 2.29297C6.30636 2.24903 6.97223 2.25 7.79979 2.25H16.2002Z"
      fill="#FF540F"
    />
    <path
      d="M14.9893 11.9512C15.2672 11.6919 15.7019 11.6798 15.9942 11.9355C16.2862 12.1912 16.3323 12.6237 16.1123 12.9336L16.0645 12.9941L16.0625 12.9961C16.0618 12.9969 16.0614 12.9981 16.0606 12.999C16.0588 13.001 16.0561 13.0032 16.0537 13.0059C16.0491 13.0111 16.0433 13.0177 16.0361 13.0254C16.0213 13.0414 16.0008 13.0631 15.9756 13.0889C15.9249 13.1406 15.8526 13.2108 15.7608 13.293C15.5773 13.4571 15.3103 13.6726 14.9668 13.8867C14.2809 14.3142 13.2697 14.75 12 14.75C10.7303 14.75 9.71914 14.3142 9.03322 13.8867C8.68975 13.6726 8.42277 13.4571 8.23927 13.293C8.1474 13.2108 8.07513 13.1406 8.02443 13.0889C7.9992 13.0631 7.9787 13.0414 7.96388 13.0254C7.95675 13.0177 7.95097 13.0111 7.9463 13.0059C7.94393 13.0032 7.94119 13.001 7.93947 12.999C7.93864 12.9981 7.93819 12.9969 7.93751 12.9961L7.93556 12.9941C7.66284 12.6825 7.69428 12.2083 8.00587 11.9355C8.31756 11.6628 8.79169 11.6943 9.06447 12.0059L9.06642 12.0078C9.07183 12.0137 9.08192 12.025 9.09669 12.04C9.12629 12.0703 9.17418 12.1176 9.23927 12.1758C9.37018 12.2928 9.56839 12.4525 9.82619 12.6133C10.3434 12.9357 11.0824 13.25 12 13.25C12.9176 13.25 13.6567 12.9357 14.1738 12.6133C14.4316 12.4525 14.6298 12.2928 14.7608 12.1758C14.8258 12.1176 14.8737 12.0703 14.9033 12.04C14.9181 12.025 14.9282 12.0137 14.9336 12.0078L14.9356 12.0059L14.9893 11.9512ZM9.25001 6.25C9.94037 6.25 10.5 6.80964 10.5 7.5C10.5 8.19036 9.94037 8.75 9.25001 8.75C8.60286 8.75 8.07094 8.25817 8.00685 7.62793L8.00001 7.5L8.00685 7.37207C8.07094 6.74183 8.60286 6.25 9.25001 6.25ZM14.75 6.25C15.4404 6.25 16 6.80964 16 7.5C16 8.19036 15.4404 8.75 14.75 8.75C14.1029 8.75 13.5709 8.25817 13.5069 7.62793L13.5 7.5L13.5069 7.37207C13.5709 6.74183 14.1029 6.25 14.75 6.25Z"
      fill="white"
    />
  </svg>
)

const ChatIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.2002 2.25C17.0278 2.25 17.6936 2.24904 18.2314 2.29297C18.778 2.33763 19.258 2.43288 19.7021 2.65918C20.4077 3.0187 20.9813 3.59225 21.3408 4.29785C21.5671 4.74205 21.6624 5.22195 21.707 5.76856C21.751 6.30639 21.75 6.97222 21.75 7.79981V13.2002C21.75 14.0278 21.751 14.6936 21.707 15.2314C21.6624 15.7781 21.5671 16.258 21.3408 16.7022C20.9813 17.4078 20.4077 17.9813 19.7021 18.3408C19.258 18.5671 18.778 18.6624 18.2314 18.707C17.6936 18.751 17.0278 18.75 16.2002 18.75H13.6836C13.0297 18.75 12.8062 18.7535 12.5996 18.7959C12.4012 18.8367 12.2087 18.9039 12.0283 18.9961C11.8406 19.092 11.6639 19.2293 11.1533 19.6377L8.76856 21.5459C8.57234 21.7029 8.38469 21.8534 8.22363 21.96C8.07408 22.0588 7.82058 22.2096 7.50098 22.21C7.12047 22.2102 6.76084 22.0366 6.52344 21.7393C6.32406 21.4895 6.28282 21.1981 6.2666 21.0195C6.24917 20.8273 6.25 20.5871 6.25 20.3359V18.7451C5.75773 18.7379 5.37292 18.7141 5.0293 18.6221C3.73535 18.2753 2.7247 17.2647 2.37793 15.9707C2.24371 15.4698 2.25 14.8817 2.25 14V7.79981C2.25 6.97222 2.24903 6.30639 2.29297 5.76856C2.33763 5.22195 2.43287 4.74205 2.65918 4.29785C3.0187 3.59226 3.59226 3.01871 4.29785 2.65918C4.74204 2.43287 5.22196 2.33763 5.76855 2.29297C6.30637 2.24903 6.97224 2.25 7.79981 2.25H16.2002Z"
      fill="#FF540F"
    />
    <path
      d="M14.9893 11.9512C15.2672 11.6919 15.7019 11.6798 15.9941 11.9355C16.2862 12.1912 16.3323 12.6237 16.1123 12.9336L16.0645 12.9941L16.0625 12.9961C16.0618 12.9969 16.0614 12.9981 16.0605 12.999C16.0588 13.001 16.0561 13.0032 16.0537 13.0059C16.049 13.0111 16.0433 13.0177 16.0361 13.0254C16.0213 13.0414 16.0008 13.0631 15.9756 13.0889C15.9249 13.1406 15.8526 13.2108 15.7607 13.293C15.5772 13.4571 15.3103 13.6726 14.9668 13.8867C14.2809 14.3142 13.2697 14.75 12 14.75C10.7303 14.75 9.71913 14.3142 9.0332 13.8867C8.68973 13.6726 8.42275 13.4571 8.23926 13.293C8.14739 13.2108 8.07511 13.1406 8.02441 13.0889C7.99918 13.0631 7.97869 13.0414 7.96387 13.0254C7.95674 13.0177 7.95096 13.0111 7.94629 13.0059C7.94392 13.0032 7.94117 13.001 7.93945 12.999C7.93863 12.9981 7.93817 12.9969 7.9375 12.9961L7.93555 12.9941C7.66283 12.6825 7.69426 12.2083 8.00586 11.9355C8.31754 11.6628 8.79167 11.6943 9.06445 12.0059L9.06641 12.0078C9.07181 12.0137 9.08191 12.025 9.09668 12.04C9.12628 12.0703 9.17416 12.1176 9.23926 12.1758C9.37017 12.2928 9.56837 12.4525 9.82617 12.6133C10.3434 12.9357 11.0824 13.25 12 13.25C12.9176 13.25 13.6566 12.9357 14.1738 12.6133C14.4316 12.4525 14.6298 12.2928 14.7607 12.1758C14.8258 12.1176 14.8737 12.0703 14.9033 12.04C14.9181 12.025 14.9282 12.0137 14.9336 12.0078L14.9355 12.0059L14.9893 11.9512ZM9.25 6.25C9.94036 6.25 10.5 6.80964 10.5 7.5C10.5 8.19036 9.94036 8.75 9.25 8.75C8.60285 8.75 8.07092 8.25817 8.00684 7.62793L8 7.5L8.00684 7.37207C8.07092 6.74183 8.60285 6.25 9.25 6.25ZM14.75 6.25C15.4404 6.25 16 6.80964 16 7.5C16 8.19036 15.4404 8.75 14.75 8.75C14.1028 8.75 13.5709 8.25817 13.5068 7.62793L13.5 7.5L13.5068 7.37207C13.5709 6.74183 14.1028 6.25 14.75 6.25Z"
      fill="white"
    />
  </svg>
)

const HideIcon = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.197 3.86358C17.4899 3.57069 17.4899 3.09582 17.197 2.80292C16.9041 2.51003 16.4292 2.51003 16.1363 2.80292L2.80301 16.1363C2.51012 16.4291 2.51012 16.904 2.80301 17.1969C3.0959 17.4898 3.57078 17.4898 3.86367 17.1969L5.6737 15.3869L5.67663 15.3886C6.96307 16.1526 8.44806 16.5833 10.0003 16.5833C13.6318 16.5833 16.8872 14.2313 18.1206 10.754C18.2931 10.2671 18.2933 9.73213 18.1206 9.2453C17.5931 7.75828 16.6968 6.47846 15.5605 5.5001L17.197 3.86358ZM14.4963 6.56432L12.7666 8.29396C13.0729 8.7897 13.25 9.37433 13.25 9.99993C13.25 11.7949 11.7949 13.2499 10 13.2499C9.37442 13.2499 8.78979 13.0728 8.29404 12.7665L6.77568 14.2849C7.75787 14.7993 8.85856 15.0833 10.0003 15.0833C12.9762 15.0833 15.6789 13.1508 16.7069 10.2525C16.7646 10.0895 16.7644 9.90883 16.7069 9.74689C16.255 8.47279 15.4785 7.38392 14.4963 6.56432ZM9.41179 11.6488C9.5956 11.7143 9.79358 11.7499 10 11.7499C10.9665 11.7499 11.75 10.9664 11.75 9.99993C11.75 9.79349 11.7144 9.59552 11.6489 9.4117L9.41179 11.6488Z"
      fill="#222222"
    />
    <path
      d="M1.87966 9.24521C3.11362 5.76789 6.36949 3.41659 10.0003 3.41659C10.7332 3.41659 11.4515 3.51256 12.141 3.69444C12.5415 3.80008 12.7806 4.2104 12.6749 4.61091C12.5693 5.01143 12.159 5.25047 11.7585 5.14483C11.1916 4.99533 10.6019 4.91659 10.0003 4.91659C7.02486 4.91659 4.32176 6.84861 3.29329 9.74685C3.23563 9.90985 3.23581 10.0899 3.29328 10.2518C3.54886 10.9722 3.90809 11.6332 4.34942 12.2198C4.59845 12.5508 4.53201 13.021 4.20102 13.27C3.87003 13.5191 3.39983 13.4526 3.1508 13.1216C2.61899 12.4148 2.1868 11.6192 1.87967 10.7536C1.7069 10.2667 1.70714 9.73209 1.87966 9.24521Z"
      fill="#222222"
    />
  </svg>
)

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.57571 13.0909C5.3414 12.8566 5.3414 12.4767 5.57571 12.2423L9.81812 7.99994L5.57572 3.75754C5.3414 3.52323 5.3414 3.14333 5.57572 2.90901C5.81003 2.6747 6.18993 2.6747 6.42424 2.90901L11.0909 7.57568C11.2034 7.6882 11.2666 7.84081 11.2666 7.99994C11.2666 8.15907 11.2034 8.31168 11.0909 8.42421L6.42424 13.0909C6.18993 13.3252 5.81003 13.3252 5.57571 13.0909Z"
      fill="#BCBCBC"
    />
  </svg>
)

const MenuItem = ({
  onClick,
  leftIcon,
  children,
  rightIcon,
}: {
  onClick: () => void
  leftIcon: React.ReactNode
  children: React.ReactNode
  rightIcon?: React.ReactNode
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 rounded-lg px-3 py-3 text-sm text-gray-900 hover:bg-gray-50 transition-colors"
    >
      {leftIcon}
      <span className="whitespace-nowrap flex-1 text-left">{children}</span>
      {rightIcon}
    </button>
  )
}

const UnniGuideFab: React.FC = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const { isLocale, locale } = useLocales()
  const { hidden, hideForDuration } = useHiddenUntil()
  const [isOpen, setIsOpen] = useState(false)
  const [isFolded, setIsFolded] = useState(true)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [fabShown, setFabShown] = useState(false)

  // Amplitude 이벤트용 공통 프로퍼티
  const getAmplitudeProps = useCallback(
    () => ({
      page_path: router.asPath,
      locale,
      country: localeToCountry(locale),
    }),
    [router.asPath, locale],
  )

  // 특정 경로 및 로케일에서는 노출하지 않음
  const disabledInRoute = useMemo(() => {
    const asPath = router.asPath || ''

    const isDisabledLocale = isLocale.KR || isLocale.JP

    return (
      isDisabledLocale ||
      asPath.startsWith('/channel-talk') ||
      asPath.includes('/unni-guide-v2') ||
      asPath.startsWith('/community') ||
      asPath.startsWith('/about') ||
      asPath.includes('/inquiry') ||
      asPath.startsWith('/signin') ||
      asPath.startsWith('/signup') ||
      asPath.startsWith('/chat') ||
      asPath.startsWith('/info') ||
      asPath.includes('/complete') ||
      asPath.startsWith('/download') ||
      asPath.startsWith('/posts')
    )
  }, [router.asPath, isLocale])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', onKey)
      return () => window.removeEventListener('keydown', onKey)
    }
  }, [])

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (!wrapperRef.current) return
      if (!wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setIsFolded(true)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  // FAB 표시 이벤트
  useEffect(() => {
    if (!fabShown && !hidden && !disabledInRoute) {
      setFabShown(true)
      postAmplitude('view_unni_guide_fab', getAmplitudeProps())
    }
  }, [fabShown, hidden, disabledInRoute, getAmplitudeProps])

  // 자동 펼치기 및 접기
  useEffect(() => {
    const expandTimer = setTimeout(() => {
      setIsFolded(false)
    }, 500)

    const foldTimer = setTimeout(() => {
      setIsFolded(true)
    }, 5000)

    return () => {
      clearTimeout(expandTimer)
      clearTimeout(foldTimer)
    }
  }, [])

  const handleToggle = () =>
    setIsOpen((v) => {
      if (!v) {
        setIsFolded(false)
        postAmplitude('click_unni_guide_fab_open', {
          ...getAmplitudeProps(),
          trigger_type: 'click',
        })
        return true
      } else {
        setIsFolded(true)
        postAmplitude('click_unni_guide_fab_close', getAmplitudeProps())
        return false
      }
    })
  const handleConsult = async () => {
    postAmplitude('click_unni_guide_fab_menu_consult', getAmplitudeProps())
    setIsOpen(false)
    await router.push('/unni-guide-v2')
  }
  const handleHide = () => {
    postAmplitude('click_unni_guide_fab_menu_hide', getAmplitudeProps())
    hideForDuration(HIDE_MS)
  }

  const handleMouseEnter = () => {
    if (!isOpen && isFolded) {
      postAmplitude('hover_unni_guide_fab_expand', getAmplitudeProps())
    }
    setIsFolded(false)
  }

  if (hidden || disabledInRoute) return null

  return (
    <div
      ref={wrapperRef}
      className="fixed bottom-[80px] right-6 sm:right-8 z-50 select-none"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => !isOpen && setIsFolded(true)}
    >
      {/* Menu */}
      <div
        id="unni-fab-menu"
        aria-hidden={!isOpen}
        className={cn(
          'absolute bottom-16 right-0 w-[232px] origin-bottom-right',
          isOpen ? 'animate-in fade-in-0 zoom-in-95' : 'hidden',
        )}
      >
        <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl p-2">
          <MenuItem onClick={handleConsult} leftIcon={<ChatIcon />} rightIcon={<ChevronRightIcon />}>
            {t('unni_guide.fab.chat_label')}
          </MenuItem>
          <div className="h-px bg-gray-100 my-1" />
          <MenuItem onClick={handleHide} leftIcon={<HideIcon />}>
            {t('unni_guide.fab.hide_button')}
          </MenuItem>
        </div>
      </div>

      {/* FAB Button */}
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls="unni-fab-menu"
        aria-label={isOpen ? '메뉴 닫기' : '언니가이드 열기'}
        onClick={handleToggle}
        className={cn(
          'h-12 rounded-full bg-white border border-gray-200 shadow-xl flex items-center px-3 transition-colors',
          'hover:bg-gray-50 focus:outline-none',
        )}
      >
        {isOpen ? <CloseIcon className="text-gray-900" /> : <UnniIcon />}

        {/* 텍스트: folded 상태에 따라 확장/축소, 텍스트가 폭을 결정 */}
        <span
          className={cn(
            'whitespace-nowrap text-sm font-medium text-gray-900 overflow-hidden transition-all duration-300',
            isFolded ? 'max-w-0 opacity-0' : 'max-w-[240px] opacity-100 pl-1',
          )}
        >
          {t('unni_guide.fab.chat_label')}
        </span>
      </button>
    </div>
  )
}

export default UnniGuideFab
